import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import './AddUserModal.scss';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data?: any) => void;
  companyId: string;
  roles: Array<{ _id: string; name: string }>;
}

interface UserFormData {
  name: string;
  email: string;
  role: string;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  roles
}) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    role: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        email: '',
        role: ''
      });
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!formData.role) {
      setError('Role is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }

      const result = await response.json();
      
      if (result.success) {
        // Reset form
        setFormData({
          name: '',
          email: '',
          role: ''
        });

        if (onSuccess) {
          onSuccess(result.data);
        }

        onClose();
      } else {
        throw new Error(result.message || 'Failed to create user');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      setError(error.message || 'Failed to create user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-v2-modal-overlay" onClick={onClose}>
      <div className="admin-v2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-v2-modal-header">
          <h2 className="admin-v2-modal-title">Add New User</h2>
          <button 
            className="admin-v2-modal-close-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <div className="admin-v2-modal-content">
          <form onSubmit={handleSubmit} className="admin-v2-modal-form">
            <div className="admin-v2-form-group">
            <label className="admin-v2-form-label">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="admin-v2-form-input"
              placeholder="Enter user name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="admin-v2-form-group">
            <label className="admin-v2-form-label">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="admin-v2-form-input"
              placeholder="Enter user email"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="admin-v2-form-group">
            <label className="admin-v2-form-label">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="admin-v2-form-input"
              required
              disabled={isSubmitting}
            >
              <option value="">Select a role</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
            {roles.length === 0 && (
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                No roles available. Please create roles first.
              </p>
            )}
          </div>

          <div style={{ 
            fontSize: '13px', 
            color: '#6b7280', 
            marginTop: '8px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <strong>Note:</strong> User will be automatically verified and will need to set a password on first login.
          </div>

          {error && (
            <div style={{ 
              color: '#dc2626', 
              fontSize: '14px', 
              marginTop: '1rem',
              padding: '8px',
              backgroundColor: '#fef2f2',
              borderRadius: '4px',
              border: '1px solid #fecaca'
            }}>
              {error}
            </div>
          )}

          {/* Modal Actions */}
          <div className="admin-v2-modal-actions" style={{ marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              className="admin-v2-modal-cancel-button"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-v2-modal-submit-button"
              disabled={isSubmitting || roles.length === 0}
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;

